/**
 * Default action content functions.
 * This file exists to break circular dependencies between action-content.tsx and edit-offer-utils.tsx
 */

import _ from 'lodash';
import { getModalContentForTemplate } from '@bbkAdminComponents/experiences/offers/content/modal-content-templates';
import {
  RestModalAction,
  RestModalTemplates,
  type RestModalCard,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type RestOffer } from '@bbkAdminUtils/api-client/rest-offers-api';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

export function getDefaultActionContent(
  actionName:
    | EnumLike<typeof RestModalAction>
    | EnumLike<typeof RestModalTemplates.final_offer>
): RestModalForwards | null {
  switch (actionName) {
    case RestModalAction.modal_email:
      return {
        action: RestModalAction.modal_email,
        text: 'Send message',
        icon: 'bbk-esi-message',
        modal: '',
        sync: {},
      };
    case RestModalAction.modal_email_structured:
      return {
        action: RestModalAction.modal_email_structured,
        text: 'Send message',
        icon: 'bbk-esi-message',
        url: '',
        modal: '',
        sync: {},
      };
    case RestModalAction.send_email:
      return {
        action: RestModalAction.send_email,
        text: 'Send message',
        icon: 'bbk-esi-message',
        url: '',
        modal: '',
        sync: {
          message: 'Message from the offer',
        },
      };
    case RestModalAction.link:
      return {
        action: RestModalAction.link,
        icon: 'none',
        text: 'Book a time',
        url: 'https://www.brightback.com',
        sync: {},
      };
    case RestModalAction.accept_offer_with_confirmation:
      return {
        action: RestModalAction.accept_offer_with_confirmation,
        icon: 'none',
        text: 'Accept offer',
        modal: '',
        sync: {},
      };
    case RestModalAction.cancel_with_link:
      return {
        action: RestModalAction.cancel_with_link,
        text: 'Cancel',
        url: 'https://www.brightback.com',
        sync: {},
      };
    case RestModalAction.nevermind:
      return {
        action: RestModalAction.nevermind,
        text: 'Go back to my account',
        url: 'https://www.brightback.com',
        sync: {},
      };
    case RestModalAction.dismiss_modal:
      return {
        action: RestModalAction.dismiss_modal,
        text: 'Continue to cancel',
        sync: {},
      };
    case RestModalTemplates.final_offer:
      // FIXME: this is not RestModalForwards, not sure if this case being used at all
      return {
        action: RestModalTemplates.final_offer,
        generic_actions: [
          {
            text: 'Accept offer',
            action: RestModalAction.link,
            url: 'https://www.brightback.com?accept_offer',
            sync: {},
          },
          {
            text: 'Decline and cancel immediately',
            action: RestModalAction.cancel_with_link,
            url: 'https://www.brightback.com?cancel',
            sync: {},
          },
        ],
      } as unknown as RestModalForwards;
    default:
      return null;
  }
}

export function getDefaultActionSteps(
  offer: RestOffer,
  newAction: EnumLike<typeof RestModalAction>
): RestModalCard[] {
  switch (newAction) {
    case RestModalAction.modal_email: {
      const sendModal = getModalContentForTemplate(
        _.snakeCase(offer.display_name),
        RestModalTemplates.modal_email_send
      )!;
      const successModal = getModalContentForTemplate(
        _.snakeCase(offer.display_name),
        RestModalTemplates.modal_email_success
      )!;
      _.set(sendModal, 'requires.forwards.modal', successModal.name);
      return [sendModal, successModal];
    }
    case RestModalAction.modal_email_structured: {
      const structuredModal = getModalContentForTemplate(
        _.snakeCase(offer.display_name),
        RestModalTemplates.modal_email_structured
      )!;
      const successModalES = getModalContentForTemplate(
        _.snakeCase(offer.display_name),
        RestModalTemplates.modal_email_success
      )!;
      _.set(structuredModal, 'requires.forwards.modal', successModalES.name);
      return [structuredModal, successModalES];
    }
    case RestModalAction.send_email: {
      const sendEmailSuccessModal = getModalContentForTemplate(
        _.snakeCase(offer.display_name),
        RestModalTemplates.modal_email_success
      )!;
      return [sendEmailSuccessModal];
    }
    case RestModalAction.accept_offer_with_confirmation: {
      const acceptSuccessModal = getModalContentForTemplate(
        _.snakeCase(offer.display_name),
        'acceptOfferSuccess'
      )!;
      return [acceptSuccessModal];
    }
    default: {
      return [];
    }
  }
}
